import React, { createContext, useContext, useEffect, useState } from 'react';
import { filterHiddenPlatforms } from 'src/components/featureFlags/featureFlagFilters';
import {
  getPlatformTiles,
  getPlatformTilesForB2C,
} from 'src/services/apiService';
import { CambridgeProduct } from 'src/types';
import useAuth from './useAuth';
import useFeatureFlags from './useFeatureFlags';

interface PlatformTilesContextValue {
  products: CambridgeProduct[];
  isLoading: boolean;
}

const PlatformTilesContext = createContext<
  PlatformTilesContextValue | undefined
>(undefined);

export const PlatformTilesProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const [products, setProducts] = useState<CambridgeProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPlatformTiles = async () => {
      if (!user) {
        setProducts([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const tiles = user.organisationId
          ? await getPlatformTiles(user.organisationId)
          : await getPlatformTilesForB2C();

        const filteredTiles = tiles.filter((tile: any) =>
          filterHiddenPlatforms(tile.platformName, isEnabled)
        );

        const mappedTiles = filteredTiles.map((tile: any) => ({
          title: tile.platformName,
          description: tile.description || '',
          imageUrl: tile.iconURL,
          url: tile.platformURL,
        }));
        setProducts(mappedTiles);
      } catch (error) {
        console.error('Error fetching platform tiles:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatformTiles();
  }, [user?.id, user?.organisationId, isEnabled]);

  return (
    <PlatformTilesContext.Provider value={{ products, isLoading }}>
      {children}
    </PlatformTilesContext.Provider>
  );
};

const usePlatformTiles = (): PlatformTilesContextValue => {
  const context = useContext(PlatformTilesContext);
  if (!context) {
    throw new Error(
      'usePlatformTiles must be used within a PlatformTilesProvider'
    );
  }
  return context;
};

export default usePlatformTiles;
